import { FULL_DATE } from '@/constants/dateFormat';
import { addMonths } from 'date-fns';
import dayjs from 'dayjs';
import * as Yup from 'yup';

/**
 * 失物招领列表
 */
export interface LoseListInfo {
  /**
   * 失物招领状态
   */
  status: string,
  /**
   * 物品关键词
   */
  item_content: string,
  /**
   * 服务编号
   */
  service_no: string,
  /**
   * 物品编号
   */
  item_no: string,
  /**
   * 挂失人手机号
   */
  phone: string;
  /**
   * 挂失人会员号
   */
  user_code: string;
  /**
   * 拾到时间(pick_start_at、pick_end_at)
   */
  time_range?: string[];
  /**
   * 是否高额物品 2是，1否
   */
  item_is_valuable?: number;
}

/**
 * 失物招领创建
 */
export interface LoseInfo {
  /**
   * 会员id 会员和非会员之间必须填一个
   */
  member_id?: string,
  /**
   * 非会员id
   */
  not_member_id?: string,
  /**
   * 服务状态
   */
  lose_status: number,
  /**
   * 记录创建人
   */
  record_creator: number,
  /**
   * 记录关闭人
   */
  record_close: number,
  /**
   * 拾到人姓名
   */
  pick_name: string,
  /**
   * 拾到时间
   */
  pick_date_at: string,
  /**
   * 拾到人所在团队id
   */
  pick_team_id: number,
  /**
   * 拾到备注
   */
  pick_remark: string,
  /**
   * 物品编号
   */
  item_no: string,
  /**
   * 物品类型
   */
  item_type: string,
  /**
   * 是否为高额物品，1是，0否
   */
  item_is_valuable: number,
  /**
   * 物品找到/失踪地点
   */
  item_pick_addr: string,
  /**
   * 存放地点id
   */
  item_deposit_addr_id: number,
  /**
   * 物品到期日
   */
  item_validity_date: string,
  /**
   * 物品描述
   */
  item_remark: string,
}

/**
 * 失物招领查询表单默认值
 */
export const defaultLoseListInfo: LoseListInfo = {
  status: '',
  item_content: '',
  service_no: '',
  item_no: '',
  phone: '',
  user_code: '',
  time_range: [],
  item_is_valuable: 1,
};

/**
 * 失物招领创建表单默认值
 */
export const defaultLoseInfo: LoseInfo = {
  member_id: '',
  not_member_id: '',
  lose_status: null,
  record_creator: null,
  record_close: null,
  pick_name: '',
  pick_date_at: '',
  pick_team_id: null,
  pick_remark: '',
  item_no: '',
  item_type: '',
  item_is_valuable: null,
  item_pick_addr: '',
  item_deposit_addr_id: null,
  // 到期日 默认= 当前时间 + 3个月
  item_validity_date: dayjs(addMonths(new Date(), 3).setHours(0, 0, 0, 0)).format(FULL_DATE),
  item_remark: '',
};

/**
 * 失物招领详情编辑表单默认值
 */
export const defaultLoseEditInfo: LoseInfo = {
  member_id: '',
  not_member_id: '',
  lose_status: null,
  record_creator: null,
  record_close: null,
  pick_name: '',
  pick_date_at: '',
  pick_team_id: null,
  pick_remark: '',
  item_no: '',
  item_type: '',
  item_is_valuable: null,
  item_pick_addr: '',
  item_deposit_addr_id: null,
  item_validity_date: '',
  item_remark: '',
};

/**
 * 失物招领表单校验
 */
export const loseSchema = Yup.object().shape(
  {
    lose_status: Yup.number().typeError('请选择服务状态').required('请选择服务状态'),
    record_creator: Yup.number().typeError('请选择记录创建人').required('请选择记录创建人'),
    /**
     * 挂失人/领取人信息
     */
    content: Yup.string().when(['lose_status', 'member_id', 'not_member_id'], {
      is: (lose_status, member_id, not_member_id) => lose_status >= 1 && lose_status <= 4 && !member_id && !not_member_id, // 如果服务状态是失物(1-4)，挂失人/领取人字段必填
      then: Yup.string().required('请输入手机号或会员号搜索挂失人/领取人信息'),
    }),
    /**
     * 拾到人信息
     */
    pick_name: Yup.string().when(['lose_status'], {
      is: (lose_status) => lose_status >= 5 && lose_status <= 9, // 如果服务状态是招领(5-9)，拾到人姓名字段必填
      then: Yup.string().required('请输入拾到人姓名'),
    }),
    /**
     * 物品信息
     */
    item_type: Yup.string().ensure().required('请选择物品类型'),
  },
  [
    ['lose_status', 'pick_name'],
    ['lose_status', 'content'],
  ]
);

/**
 * 失物招领详情编辑表单校验
 */
export const loseEditSchema = Yup.object().shape(
  {
    lose_status: Yup.number().typeError('请选择服务状态').required('请选择服务状态'),
    record_creator: Yup.number().typeError('请选择记录创建人').required('请选择记录创建人'),
    /**
     * 挂失人/领取人信息
     */
    user_name: Yup.string().when(['lose_status', 'member_id', 'not_member_id'], {
      is: (lose_status, member_id, not_member_id) => lose_status >= 1 && lose_status <= 4 && !member_id && !not_member_id, // 如果服务状态是失物(1-4)，挂失人/领取人字段必填
      then: Yup.string().required('请输入手机号或会员号搜索挂失人/领取人信息'),
    }),
    /**
     * 拾到人信息
     */
    pick_name: Yup.string().when(['lose_status'], {
      is: (lose_status) => lose_status >= 5 && lose_status <= 9, // 如果服务状态是招领(5-9)，拾到人姓名字段必填
      then: Yup.string().required('请输入拾到人姓名'),
    }),
    /**
     * 物品信息
     */
    item_type: Yup.string().ensure().required('请选择物品类型'),
  },
  [
    ['pick_name', 'pick_date_at'],
    ['pick_name', 'pick_team_id'],
    ['pick_name', 'pick_remark'],
    ['pick_date_at', 'pick_team_id'],
    ['pick_date_at', 'pick_remark'],
    ['pick_team_id', 'pick_remark'],
  ]
);